/**
 * SPEC-AgentDetail-Revisions — Delivery Details card (renamed in the header
 * from "Delivery details" to "Delivery Details").
 *
 * Field list (locked 2026-05-29):
 *   - Company
 *   - Recipient Name
 *   - Number, Street Name, City, State    (single combined address line)
 *   - Destination Notes                   (with "RECIPIENT · READ-ONLY" tag)
 *
 * SPEC-AgentView-PickupContext (2026-05-31) adds:
 *   - Task-type chip ("Pickup Task" / "Delivery Task") in the card header,
 *     right-aligned next to "Delivery Details".
 *   - Notes row between Address and Destination Notes, rendered only when
 *     `taskNotes` is truthy. Surfaces dispatcher's OnFleet task notes —
 *     e.g. pickup instructions on QATT_4, delivery instructions on
 *     non-CU dropoffs.
 *
 * The previous fields (Drop zone / Driver / Driver completion notes /
 * Pickup hub / Bag notes / Scan ID) are intentionally removed — they
 * either duplicate the hero (Driver Name moved to the hero chip stack)
 * or migrated to the new Completion Details card (Scan ID + driver
 * completion notes + OnFleet / Slack buttons).
 *
 * The senior surface keeps its own richer ChecklistTile inside
 * `qa-detail-v3.tsx` for the one-cycle legacy delegation. This component
 * is the slim shared one the agent layout mounts.
 */

import { Starling } from "@/lib/starling-brand";

export interface ChecklistCardProps {
  company?: string | null;
  recipientName?: string | null;
  address?: string | null;
  destinationNotes?: string | null;
  // SPEC-AgentView-PickupContext — dispatcher's OnFleet task notes. Renders
  // a "NOTES" row between Address and Destination Notes when truthy.
  taskNotes?: string | null;
  // SPEC-AgentView-PickupContext — task-type indicator. Renders a small
  // chip in the card header. `null` hides the chip.
  taskType?: "pickup" | "delivery" | null;
}

const TOKENS = {
  card: "#FFFFFF",
  sub: "#F3F4F6",
  border: "#E5E7EB",
  text: "#111827",
  txt2: "#6B7280",
  txt3: "#9CA3AF",
  readonly: "#475569",
  readonlyBg: "#F1F5F9",
  // SPEC-AgentView-PickupContext — pickup chip uses brand purple, delivery
  // chip uses the same neutral grey as the read-only tag for visual
  // consistency.
  pickupChipBg: "rgba(89, 47, 150, 0.10)",
  pickupChipColor: Starling.purple,
  deliveryChipBg: "#F1F5F9",
  deliveryChipColor: "#475569",
} as const;

/**
 * SPEC-AgentView-PickupContext — task-type chip. Renders nothing when
 * `type` is null, so the call site doesn't need to guard.
 */
function TaskTypeChip({ type }: { type: "pickup" | "delivery" | null }) {
  if (type == null) return null;
  const isPickup = type === "pickup";
  const label = isPickup ? "Pickup Task" : "Delivery Task";
  const bg = isPickup ? TOKENS.pickupChipBg : TOKENS.deliveryChipBg;
  const color = isPickup ? TOKENS.pickupChipColor : TOKENS.deliveryChipColor;
  return (
    <span
      data-testid="checklist-task-type-chip"
      data-task-type={type}
      style={{
        fontSize: 11,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        background: bg,
        color,
        borderRadius: 4,
        padding: "2px 8px",
        lineHeight: 1.4,
      }}
    >
      {label}
    </span>
  );
}

export function ChecklistCard({
  company,
  recipientName,
  address,
  destinationNotes,
  taskNotes,
  taskType,
}: ChecklistCardProps) {
  // SPEC-AgentView-PickupContext — treat whitespace-only taskNotes as
  // absent so the row stays hidden when there's nothing to show.
  const taskNotesValue =
    typeof taskNotes === "string" && taskNotes.trim().length > 0
      ? taskNotes
      : null;
  const taskTypeValue = taskType ?? null;
  return (
    <div
      data-testid="card-qa-checklist"
      style={{
        background: TOKENS.card,
        border: `1px solid ${TOKENS.border}`,
        borderRadius: 8,
        padding: "12px 16px",
        boxShadow: "0 1px 2px rgba(16,24,40,.06)",
        fontFamily: Starling.fontBody,
        color: TOKENS.text,
      }}
    >
      <div
        style={{
          fontFamily: Starling.fontHead,
          fontSize: 14,
          fontWeight: 600,
          marginBottom: 10,
          display: "flex",
          alignItems: "center",
          gap: 9,
        }}
      >
        <span aria-hidden style={{ width: 18, height: 18, borderRadius: 4, background: TOKENS.sub, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: TOKENS.txt2 }}>📋</span>
        <span>Delivery Details</span>
        <span style={{ flex: 1 }} />
        <TaskTypeChip type={taskTypeValue} />
      </div>
      <Row label="Company" value={company ?? "—"} />
      <Row label="Recipient Name" value={recipientName ?? "—"} />
      <Row label="Number, Street Name, City, State" value={address ?? "—"} />
      {taskNotesValue && (
        <div
          data-testid="checklist-row-notes"
          style={{ padding: "9px 0", borderBottom: `1px solid ${TOKENS.border}` }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: 10,
              color: TOKENS.txt2,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              fontWeight: 700,
              marginBottom: 3,
            }}
          >
            <span>Notes</span>
          </div>
          <div
            style={{
              fontSize: 13.5,
              fontWeight: 500,
              lineHeight: 1.3,
              color: TOKENS.text,
              whiteSpace: "pre-line",
            }}
          >
            {taskNotesValue}
          </div>
        </div>
      )}
      <Row label="Destination Notes" value={destinationNotes ?? "—"} readOnlyTag />
    </div>
  );
}

function Row({
  label,
  value,
  readOnlyTag,
}: {
  label: string;
  value: string;
  readOnlyTag?: boolean;
}) {
  return (
    <div
      data-testid="checklist-info-row"
      style={{ padding: "9px 0", borderBottom: `1px solid ${TOKENS.border}` }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          fontSize: 10,
          color: TOKENS.txt2,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          fontWeight: 700,
          marginBottom: 3,
        }}
      >
        <span>{label}</span>
        {readOnlyTag && (
          <span
            data-testid="checklist-readonly-tag"
            style={{
              fontSize: 9,
              fontWeight: 700,
              color: TOKENS.readonly,
              background: TOKENS.readonlyBg,
              borderRadius: 3,
              padding: "1px 5px",
              letterSpacing: "0.05em",
            }}
          >
            RECIPIENT · READ-ONLY
          </span>
        )}
      </div>
      <div
        style={{
          fontSize: 13.5,
          fontWeight: 500,
          lineHeight: 1.3,
          color: TOKENS.text,
        }}
      >
        {value}
      </div>
    </div>
  );
}

export default ChecklistCard;
